import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const ModalCadastroCorretor = ({
  open,
  onClose,
  loading = false,
  corretoresCv = [],
  corretoresCadastrados = new Set(),
  cadastroSucesso = new Set(),
  cadastroEmAndamento = new Set(),
  cadastroError = "",
  onCadastrar = () => {},
  imobiliariasPorCorretor = {},
}) => {
  const normalizedCadastrados = useMemo(() => {
    const base = corretoresCadastrados instanceof Set
      ? corretoresCadastrados
      : new Set(corretoresCadastrados || []);
    const sucesso = cadastroSucesso instanceof Set
      ? cadastroSucesso
      : new Set(cadastroSucesso || []);
    return new Set(
      [...base, ...sucesso].map((id) => (id != null ? String(id) : id))
    );
  }, [corretoresCadastrados, cadastroSucesso]);

  const normalizedEmAndamento = useMemo(() => {
    const base = cadastroEmAndamento instanceof Set
      ? cadastroEmAndamento
      : new Set(cadastroEmAndamento || []);
    return new Set([...base].map((id) => (id != null ? String(id) : id)));
  }, [cadastroEmAndamento]);

  const cadastroSucessoCount = useMemo(() => {
    const base = cadastroSucesso instanceof Set
      ? cadastroSucesso
      : new Set(cadastroSucesso || []);
    return base.size;
  }, [cadastroSucesso]);

  const getImobiliariasLabel = (idCorretor) => {
    const key = idCorretor != null ? String(idCorretor) : "";
    const lista = imobiliariasPorCorretor?.[key] || [];
    if (!lista.length) return "-";
    return lista.join(", ");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          pb: 1,
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Cadastrar novos corretores
        </Typography>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          bgcolor: "background.paper",
          color: "text.primary",
          borderColor: "divider",
        }}
      >
        {cadastroError && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            {cadastroError}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress size={36} />
            <Typography sx={{ mt: 2, color: "text.secondary" }}>
              Carregando corretores...
            </Typography>
          </Box>
        ) : corretoresCv.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum corretor disponivel para cadastro.
            </Typography>
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "text.primary", borderColor: "divider" }}>Nome</TableCell>
                  <TableCell sx={{ color: "text.primary", borderColor: "divider" }}>Imobiliarias</TableCell>
                  <TableCell align="right" sx={{ color: "text.primary", borderColor: "divider" }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {corretoresCv.map((corretor, index) => {
                  const idCorretor =
                    corretor?.idCorretor ?? corretor?.id ?? corretor?.id_corretor;
                  const idCorretorKey = idCorretor != null ? String(idCorretor) : "";
                  const isCadastrado = normalizedCadastrados.has(idCorretorKey);
                  const isCadastrando = normalizedEmAndamento.has(idCorretorKey);
                  const buttonLabel = isCadastrando
                    ? "Cadastrando..."
                    : isCadastrado
                    ? "Cadastrado"
                    : "Cadastrar";
                  const buttonStyles = isCadastrado || isCadastrando
                    ? {
                        bgcolor: "action.disabledBackground",
                        color: "text.primary",
                        "&:hover": { bgcolor: "action.disabledBackground" },
                        cursor: "default",
                      }
                    : {
                        bgcolor: "#2e7d32",
                        color: "#fff",
                        "&:hover": { bgcolor: "#1b5e20" },
                      };

                  return (
                    <TableRow
                      key={`${idCorretorKey || corretor?.nome || "corretor"}-${index}`}
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.03)"
                              : "rgba(0,0,0,0.02)",
                        },
                        "& td": {
                          borderColor: "divider",
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" color="text.primary">
                          {corretor?.nome || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getImobiliariasLabel(idCorretor)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => onCadastrar(corretor)}
                          disabled={isCadastrado || isCadastrando}
                          sx={{ minWidth: 120, ...buttonStyles }}
                        >
                          {isCadastrando && (
                            <CircularProgress
                              size={16}
                              sx={{ mr: 1, color: isCadastrado ? "text.primary" : "#fff" }}
                            />
                          )}
                          {buttonLabel}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {cadastroSucessoCount > 0 && (
              <Alert severity="success" variant="outlined" sx={{ mt: 2 }}>
                {cadastroSucessoCount === 1
                  ? "Corretor cadastrado com sucesso."
                  : `${cadastroSucessoCount} corretores cadastrados com sucesso.`}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalCadastroCorretor;
